import {
  ROADMAP_BASIC_KEY,
  ROADMAP_FUNDING_KEY,
  ROADMAP_KEY,
  ROADMAP_TEMPO_REPORT_BY_USER_KEY,
} from '@/domain/roadmap/hooks';
import { type BasicRoadmap, type RoadmapListDetail, type RoadMapUpdate } from '@/domain/roadmap/types';
import { USE_USER_WITH_AVAILABLE_HOURS_KEY } from '@/domain/user/hooks';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Synchronously patches all ROADMAP_KEY list caches with the fields we already
 * know changed from the saved payload. Fields with incompatible shapes between
 * RoadMapUpdate and RoadmapListDetail (product, fundingSources) are left as-is
 * and will be refreshed by the subsequent background invalidation.
 */
export function patchRoadmapListCaches(queryClient: QueryClient, saved: RoadMapUpdate): void {
  const patch: Partial<RoadmapListDetail> = {
    item: saved.item,
    locked: saved.locked,
    idOwner: saved.idOwner,
    portfolioId: saved.portfolioId,
    priority: saved.priority,
    category: saved.category,
    origin: saved.origin,
    responsibleIds: saved.responsibleIds,
    owner: saved.owner ?? undefined,
    fundingSources: saved.fundingSources.map((f) => ({
      idFundingSource: f.idFundingSource,
      percentage: f.percentage,
      name: f.fundingSource.name,
      alias: f.fundingSource.alias,
    })),
    version:
      saved.version === null
        ? undefined
        : {
            idPlatformVersion: saved.version.idPlatformVersion,
            idPlatformRelease: saved.version.idPlatformRelease,
            name: saved.version.name,
            releaseDate: saved.version.releaseDate,
            namePlatformRelease: saved.version.namePlatformRelease,
          },
    status:
      saved.status === null
        ? undefined
        : {
            idRoadmapStatus: saved.status.idRoadmapStatus,
            name: saved.status.name,
            startDate: saved.status.startDate,
            endDate: saved.status.endDate,
            isRelease: saved.status.isRelease,
          },
  };

  if (saved.type !== null) {
    patch.type = saved.type;
  }

  queryClient.setQueriesData<RoadmapListDetail[]>(
    { queryKey: ['roadmap', ROADMAP_KEY] },
    (old) => old?.map((r) => (r.idRoadmap === saved.idRoadmap ? { ...r, ...patch } : r)),
  );

  queryClient.setQueriesData<BasicRoadmap[]>(
    { queryKey: ['roadmap', ROADMAP_BASIC_KEY] },
    (old) => old?.map((r) => (r.idRoadmap === saved.idRoadmap ? { ...r, item: saved.item } : r)),
  );
}

/**
 * Invalidates all TanStack Query caches related to roadmaps.
 * Awaits the main roadmap list; fires the rest in parallel without blocking.
 */
export async function invalidateRoadmapQueries(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ['roadmap', ROADMAP_KEY] });

  void Promise.all([
    queryClient.invalidateQueries({ queryKey: ['roadmap', ROADMAP_BASIC_KEY] }),
    queryClient.invalidateQueries({ queryKey: ['roadmap', ROADMAP_FUNDING_KEY] }),
    queryClient.invalidateQueries({ queryKey: ['roadmap', ROADMAP_TEMPO_REPORT_BY_USER_KEY] }),
    queryClient.invalidateQueries({ queryKey: ['roadmap-tempo'] }),
    queryClient.invalidateQueries({ queryKey: ['resource', 'resource-groups'] }),
    queryClient.invalidateQueries({ queryKey: [USE_USER_WITH_AVAILABLE_HOURS_KEY] }),
  ]);
}
